import * as React from 'react'
import { pickFirstEligible } from './pick-first-eligible'
import type { BannerCandidate } from './pick-first-eligible'

export interface LatchableCandidate extends BannerCandidate {
  id: string
  /** Opt into the win-latch (see `useLatchedWinner`); candidates without it hide the moment they drop. */
  latchWhileActive?: boolean
}

/**
 * `pickFirstEligible` with a latch: once a `latchWhileActive` candidate wins it keeps the slot even
 * after its own eligibility drops mid-flow, and only lets go when it calls the `releaseLatch` handed to
 * `buildCandidates` — wire that to the banner's `onDismiss`. The next eligible candidate then takes
 * over, exactly like a live banner unmounting.
 */
export function useLatchedWinner<T extends LatchableCandidate>(
  buildCandidates: (releaseLatch: () => void) => readonly T[],
): T | null {
  const [latchedId, setLatchedId] = React.useState<string | null>(null)
  const releaseLatch = React.useCallback(() => setLatchedId(null), [])

  const winner = pickFirstEligible(
    buildCandidates(releaseLatch).map(candidate => ({
      ...candidate,
      isEligible:
        candidate.isEligible || (candidate.latchWhileActive === true && candidate.id === latchedId),
    })),
  )

  if (winner?.latchWhileActive === true && latchedId === null) {
    setLatchedId(winner.id)
  }

  return winner
}
